import React from 'react';
import { FaChartLine, FaClipboardList, FaRocket, FaFileAlt, FaBell, FaUserCheck, FaArrowRight, FaCrown, FaGem, FaStar, FaCheckCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';

const MemberBenefits = ({ contentData }) => {
const benefits = [
  {
    icon: <FaChartLine className="text-2xl" />,
    title: contentData?.mb_card1_title,
    features: [
      contentData?.mb_card1_text1,
      contentData?.mb_card1_text2,
      contentData?.mb_card1_text3,
      contentData?.mb_card1_text4
    ],
    color: "from-blue-500 to-blue-600",
    gradient: "bg-gradient-to-r from-gray-800 to-blue-900/20",
    animation: "animate-pulse",
    iconAnim: "animate-bounce-slow"
  },
  {
    icon: <FaClipboardList className="text-2xl" />,
    title: contentData?.mb_card2_title,
    features: [
      contentData?.mb_card2_text1,
      contentData?.mb_card2_text2,
      contentData?.mb_card2_text3,
      contentData?.mb_card2_text4
    ],
    color: "from-green-500 to-green-600",
    gradient: "bg-gradient-to-r from-gray-800 to-green-900/20",
    animation: "animate-float",
    iconAnim: "animate-pulse animation-delay-500"
  },
  {
    icon: <FaRocket className="text-2xl" />,
    title: contentData?.mb_card3_title,
    features: [
      contentData?.mb_card3_text1,
      contentData?.mb_card3_text2,
      contentData?.mb_card3_text3,
      contentData?.mb_card3_text4
    ],
    color: "from-purple-500 to-purple-600",
    gradient: "bg-gradient-to-r from-gray-800 to-purple-900/20",
    animation: "animate-bounce-slow animation-delay-200",
    iconAnim: "animate-rocket-launch"
  },
  {
    icon: <FaFileAlt className="text-2xl" />,
    title: contentData?.mb_card4_title,
    features: [
      contentData?.mb_card4_text1,
      contentData?.mb_card4_text2,
      contentData?.mb_card4_text3,
      contentData?.mb_card4_text4
    ],
    color: "from-orange-500 to-orange-600",
    gradient: "bg-gradient-to-r from-gray-800 to-orange-900/20",
    animation: "animate-pulse animation-delay-1000",
    iconAnim: "animate-float animation-delay-300"
  },
  {
    icon: <FaBell className="text-2xl" />,
    title: contentData?.mb_card5_title,
    features: [
      contentData?.mb_card5_text1,
      contentData?.mb_card5_text2,
      contentData?.mb_card5_text3,
      contentData?.mb_card5_text4
    ],
    color: "from-pink-500 to-pink-600",
    gradient: "bg-gradient-to-r from-gray-800 to-pink-900/20",
    animation: "animate-bounce-slow animation-delay-400",
    iconAnim: "animate-pulse animation-delay-700"
  },
  {
    icon: <FaUserCheck className="text-2xl" />,
    title: contentData?.mb_card6_title,
    features: [
      contentData?.mb_card6_text1,
      contentData?.mb_card6_text2,
      contentData?.mb_card6_text3,
      contentData?.mb_card6_text4
    ],
    color: "from-red-500 to-red-600",
    gradient: "bg-gradient-to-r from-gray-800 to-red-900/20",
    animation: "animate-pulse animation-delay-800",
    iconAnim: "animate-float animation-delay-200"
  }
];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-900 relative overflow-hidden" id='member-benefits'>
      {/* Background Animated Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Floating Stock Market Symbols */}
        <motion.div
          animate={{ y: [0, -20, 0], x: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity }}
          className="absolute top-20 left-10 text-4xl opacity-5"
        >
          📊
        </motion.div>
        
        <motion.div
          animate={{ y: [0, 15, 0], rotate: [0, 180, 360] }}
          transition={{ duration: 8, repeat: Infinity, delay: 1 }}
          className="absolute bottom-1/4 right-20 text-5xl opacity-5"
        >
          📈
        </motion.div>
        
        {/* Animated Chart Dots */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                y: [0, -15, 0],
                scale: [1, 1.3, 1]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                delay: i * 0.2,
                ease: "easeInOut"
              }}
              className={`absolute w-2 h-2 ${
                i % 3 === 0 ? 'bg-blue-500' : 
                i % 3 === 1 ? 'bg-green-500' : 'bg-purple-500'
              } rounded-full opacity-20`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        
        {/* Animated Lines Grid */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `
            linear-gradient(90deg, transparent 95%, #3b82f6 100%),
            linear-gradient(0deg, transparent 95%, #8b5cf6 100%)
          `,
          backgroundSize: '60px 60px',
          backgroundPosition: '0 0'
        }}>
          <motion.div
            animate={{ x: [0, 60, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-500/10 to-transparent"
          />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Animated Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          {/* Animated Premium Badge */}
          <motion.div
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-400/30 px-6 py-3 rounded-full mb-6 shadow-lg"
          >
            <FaCrown className="text-yellow-400 animate-pulse" />
            <span className="text-white font-bold">EXCLUSIVE MEMBER BENEFITS</span>
            <FaGem className="text-purple-400 animate-pulse animation-delay-500" />
          </motion.div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Your Member Benefits
          </h2>
          
          {/* Animated Underline */}
          <motion.div
            initial={{ width: 0 }}
            whileInView={{ width: "100px" }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mb-4 rounded-full"
          />
          
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            As a premium member, you get exclusive access to premium research and insights
          </p>
        </motion.div>

        {/* Animated Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              viewport={{ once: true }}
              whileHover={{ 
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
              }}
              className={`${benefit.gradient} p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-700 relative overflow-hidden group`}
            >
              {/* Animated Background Effect - REMOVED MULTIPLE ZOOM EFFECT */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              
              {/* Floating Animation Behind */}
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  opacity: [0.1, 0.3, 0.1]
                }}
                transition={{ 
                  duration: 4, 
                  repeat: Infinity,
                  delay: index * 0.5
                }}
                className={`absolute -right-8 -bottom-8 w-24 h-24 bg-gradient-to-r ${benefit.color} rounded-full opacity-10 blur-md`}
              />

              <div className="flex items-center gap-3 mb-4 relative z-10">
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.1 }}
                  className={`w-12 h-12 ${benefit.iconAnim} bg-gradient-to-r ${benefit.color} rounded-xl flex items-center justify-center shadow-lg`}
                >
                  <div className="text-white text-2xl">
                    {benefit.icon}
                  </div>
                </motion.div>
                <h3 className="text-lg font-bold text-white">
                  {benefit.title}
                </h3>
              </div>

              <div className="space-y-3 mt-4 relative z-10">
                {benefit.features.map((feature, featureIndex) => (
                  <motion.div
                    key={featureIndex}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: featureIndex * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 5 }}
                    className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-700/30 transition-colors mb-0"
                  >
                    <motion.div
                      whileHover={{ scale: 1.3 }}
                      className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0"
                    />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </motion.div>
                ))}
              </div>
              
              {/* Animated Arrow Indicator */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileHover={{ opacity: 1, x: 0 }}
                className="absolute bottom-4 right-4 text-blue-400"
              >
                <FaArrowRight className="text-sm" />
              </motion.div>
            </motion.div>
          ))}
        </div>

        

        {/* Premium Badge - Dark Theme */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          viewport={{ once: true }}
          className="relative mt-12 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-8 text-center border border-gray-700 shadow-xl overflow-hidden group"
        >
          {/* Background Glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          {/* Animated Border */}
          <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-blue-500/30 transition-all duration-300"></div>

          {/* Badge Header */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500/10 to-purple-500/10 px-5 py-2 rounded-full mb-6 border border-blue-500/20">
            <FaStar className="text-yellow-400" />
            <span className="font-bold text-white text-sm tracking-wide">PREMIUM ACCESS</span>
            <FaGem className="text-purple-400" />
          </div>
          
          {/* Title */}
          <h3 className="text-2xl font-bold text-white mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              {contentData?.mb_pa_title}
            </span>
          </h3>
          
          {/* Features */}
          <div className="flex justify-center items-center gap-3 mb-5">
            <div className="flex items-center gap-2 text-sm">
              <FaCheckCircle className="text-green-400" />
              <span className="text-gray-300">{contentData?.mb_pa_text1}</span>
            </div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="flex items-center gap-2 text-sm">
              <FaCheckCircle className="text-green-400" />
              <span className="text-gray-300">{contentData?.mb_pa_text2}</span>
            </div>
            <div className="w-1 h-1 bg-gray-600 rounded-full"></div>
            <div className="flex items-center gap-2 text-sm">
              <FaCheckCircle className="text-green-400" />
              <span className="text-gray-300">{contentData?.mb_pa_text3}</span>
            </div>
          </div>
          
          {/* Description */}
          <p className="text-gray-400 max-w-2xl mx-auto text-sm leading-relaxed">
            {contentData?.mb_pa_info}
          </p>
          
          {/* Decorative Elements */}
          <div className="absolute top-3 left-3 w-3 h-3 bg-blue-500/20 rounded-full"></div>
          <div className="absolute top-3 right-3 w-3 h-3 bg-purple-500/20 rounded-full"></div>
          <div className="absolute bottom-3 left-3 w-3 h-3 bg-blue-500/20 rounded-full"></div>
          <div className="absolute bottom-3 right-3 w-3 h-3 bg-purple-500/20 rounded-full"></div>
        </motion.div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes pulse-soft {
          0%, 100% {
            opacity: 0.8;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
        }
        
        @keyframes rocket-launch {
          0% {
            transform: translateY(0) rotate(0);
          }
          50% {
            transform: translateY(-5px) rotate(-5deg);
          }
          100% {
            transform: translateY(0) rotate(0);
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse-soft 2s ease-in-out infinite;
        }
        
        .animate-rocket-launch {
          animation: rocket-launch 1.5s ease-in-out infinite;
        }
        
        .animation-delay-200 {
          animation-delay: 200ms;
        }
        
        .animation-delay-300 {
          animation-delay: 300ms;
        }
        
        .animation-delay-400 {
          animation-delay: 400ms;
        }
        
        .animation-delay-500 {
          animation-delay: 500ms;
        }
        
        .animation-delay-700 {
          animation-delay: 700ms;
        }
        
        .animation-delay-1000 {
          animation-delay: 1000ms;
        }
      `}</style>
    </section>
  );
};

export default MemberBenefits;